import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatErr } from "../../../../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../components/ui/select";
import { toast } from "sonner";
import { ActionLink, ActionsCell, ConfirmDeleteDialog, EmptyState, SearchBox, SectionCard, StatusPill, useAccountingPermissions } from "./shared";
import { ledgerBlank, ledgerGroupLabels } from "./constants";
import { ACCOUNTING_BASE } from "./accountingNav";
import LedgerForm from "./LedgerForm";

// Ledgers are the real Phase 3 engine ledgers (/society/accounting/engine/ledgers),
// not the old Phase 1/2 society_accounting_ledgers-as-a-flat-list page this file
// used to render. System ledgers (Main bank, Cash, etc.) are read-only here -
// they can be viewed but never edited or deleted.
export default function LedgersPage() {
  const [params] = useSearchParams();
  const presetGroup = params.get("group") || "";
  const [ledgers, setLedgers] = useState([]);
  const [accountGroups, setAccountGroups] = useState([]);
  const [search, setSearch] = useState("");
  const [groupFilter, setGroupFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("active");
  const [form, setForm] = useState({ ...ledgerBlank, accountGroupId: presetGroup });
  const [showAddForm, setShowAddForm] = useState(!!presetGroup);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const perms = useAccountingPermissions();

  const load = async () => {
    try {
      const [ledgersRes, groupsRes] = await Promise.all([
        api.get("/society/accounting/engine/ledgers?includeInactive=true&annotate=true").catch(() => ({ data: [] })),
        api.get("/society/accounting/engine/account-groups").catch(() => ({ data: [] })),
      ]);
      setLedgers(Array.isArray(ledgersRes.data) ? ledgersRes.data : []);
      setAccountGroups(Array.isArray(groupsRes.data) ? groupsRes.data : []);
    } catch (err) {
      toast.error(formatErr(err, "Could not load ledgers"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const groupNameById = useMemo(() => Object.fromEntries(accountGroups.map((g) => [g.id, g.name])), [accountGroups]);

  const filtered = useMemo(() => ledgers.filter((l) => {
    if (statusFilter === "active" && l.active === false) return false;
    if (statusFilter === "inactive" && l.active !== false) return false;
    if (groupFilter !== "all" && l.ledgerGroup !== groupFilter) return false;
    if (search.trim() && !String(l.name || "").toLowerCase().includes(search.trim().toLowerCase())) return false;
    return true;
  }), [ledgers, search, groupFilter, statusFilter]);

  const toggleActive = async (ledger) => {
    try {
      await api.patch(`/society/accounting/engine/ledgers/${ledger.id}`, { active: !(ledger.active !== false) });
      toast.success(ledger.active === false ? "Ledger reactivated" : "Ledger deactivated");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not update ledger status"));
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await api.delete(`/society/accounting/engine/ledgers/${deleteTarget.id}`);
      toast.success("Ledger deleted");
      setDeleteTarget(null);
      await load();
    } catch (err) {
      toast.error(formatErr(err, "This ledger cannot be deleted because accounting records are linked to it. Deactivate it instead."));
    } finally {
      setDeleting(false);
    }
  };

  const createLedger = async () => {
    if (!form.name.trim()) { toast.error("Ledger name is required"); return; }
    try {
      setBusy(true);
      await api.post("/society/accounting/engine/ledgers", { ...form, accountGroupId: form.accountGroupId || null });
      toast.success("Ledger created");
      setForm(ledgerBlank);
      setShowAddForm(false);
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not create ledger"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-extrabold text-[#111827]">Ledgers</h2>
          <p className="mt-1 text-sm text-[#6B7280]">Every ledger used in vouchers - bank, cash, vendor, income, expense, asset and fund. Backed by the accounting engine.</p>
        </div>
        {!showAddForm && perms.canCreate && (
          <button type="button" onClick={() => setShowAddForm(true)} className="rounded-sm bg-[#111827] px-4 py-2 text-sm font-semibold text-white hover:bg-[#374151]">
            + Add ledger
          </button>
        )}
      </div>

      {showAddForm && (
        <SectionCard title="Add ledger">
          <LedgerForm form={form} setForm={setForm} accountGroups={accountGroups} groupLocked={false} busy={busy} onSubmit={createLedger}
            onCancel={() => { setShowAddForm(false); setForm(ledgerBlank); }} submitLabel="Add ledger" isEdit={false} />
        </SectionCard>
      )}

      <SectionCard>
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <SearchBox value={search} onChange={setSearch} placeholder="Search ledger name..." />
          <Select value={groupFilter} onValueChange={setGroupFilter}>
            <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All groups</SelectItem>
              {Object.entries(ledgerGroupLabels).map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="text-sm text-[#6B7280]">Loading...</div>
        ) : filtered.length === 0 ? (
          <EmptyState>No ledgers match this filter.</EmptyState>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E5E7EB] text-left text-xs uppercase tracking-[0.12em] text-[#6B7280]">
                  <th className="py-2 pr-3">Ledger name</th>
                  <th className="py-2 pr-3">Code</th>
                  <th className="py-2 pr-3">Group</th>
                  <th className="py-2 pr-3">Type</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Linked entity</th>
                  <th className="py-2 pr-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((ledger) => (
                  <tr key={ledger.id} className="border-b border-[#F3F4F6]">
                    <td className="py-2 pr-3 font-semibold text-[#111827]">{ledger.name}{ledger.isSystem && <span className="ml-2 rounded-sm bg-[#F3F4F6] px-1.5 py-0.5 text-[10px] font-semibold uppercase text-[#6B7280]">System</span>}</td>
                    <td className="py-2 pr-3">{ledger.code || "-"}</td>
                    <td className="py-2 pr-3">{groupNameById[ledger.accountGroupId] || "-"}</td>
                    <td className="py-2 pr-3">{ledgerGroupLabels[ledger.ledgerGroup] || ledger.ledgerGroup}</td>
                    <td className="py-2 pr-3"><StatusPill active={ledger.active !== false} /></td>
                    <td className="py-2 pr-3 text-[#6B7280]">{ledger.linkedEntity || "-"}</td>
                    <td className="py-2 pr-3">
                      <ActionsCell>
                        <ActionLink to={`${ACCOUNTING_BASE}/ledgers/${ledger.id}`}>View</ActionLink>
                        {!ledger.isSystem && (
                          <>
                            {perms.canCreate && (
                              <>
                                <span className="text-[#D1D5DB]">|</span>
                                <ActionLink to={`${ACCOUNTING_BASE}/ledgers/${ledger.id}/edit`}>Edit</ActionLink>
                                <span className="text-[#D1D5DB]">|</span>
                                <ActionLink tone={ledger.active === false ? "default" : "danger"} onClick={() => toggleActive(ledger)}>
                                  {ledger.active === false ? "Reactivate" : "Deactivate"}
                                </ActionLink>
                              </>
                            )}
                            {ledger.canDelete && perms.canManageMasters && (
                              <>
                                <span className="text-[#D1D5DB]">|</span>
                                <ActionLink tone="danger" onClick={() => setDeleteTarget(ledger)}>Delete</ActionLink>
                              </>
                            )}
                          </>
                        )}
                      </ActionsCell>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        title="Delete ledger?"
        identity={deleteTarget?.name}
        safeMessage="This ledger has no accounting transactions and can be permanently deleted."
        busy={deleting}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
